import { banks_logos } from "@/assets/images/banks";
import { PaymentMethod } from "@/queries/payment-method/types";
import { cn } from "@/utils/classnames";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import { Edit, Info, Loader2 } from "lucide-react";
import { Button } from "../ui/button-tw";
import { UpdatePaymentMethod } from "./PaymentCard";
import useUpdateDefaultPaymentMethod from "@/queries/payment-method/use-update-default-payment-method";
import { toast } from "react-toastify";
import useUpdatePaymentMethod from "@/queries/payment-method/use-update-payment-method";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import PaymentMethodButtonStyled from "./PaymentMethodButton";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import useDisconnectPaymentMethod from "@/queries/payment-method/use-disconnect-payment-method";
import { usePaymentMethods } from "@/queries/payment-method/use-payment-methods";
import { capitalizeFirstLetter } from "@/utils/string-manipulation";
import { usePlatformData } from "@/hooks/platform/use-platform-data";
import Plaid from "./Plaid";

type PaymentMethodCardProps = {
  paymentMethod: PaymentMethod;
  isAdmin?: boolean;
};

export function PaymentMethodCard({ paymentMethod, isAdmin = false }: PaymentMethodCardProps) {
  const platformQuery = usePlatformData();
  const paymentMethodsQuery = usePaymentMethods(paymentMethod.user_id);
  const updateDefaultPaymentMethod = useUpdateDefaultPaymentMethod();
  const [addNicknameModalOpen, setAddNicknameModalOpen] = useState(false);
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const [plaidOpen, setPlaidOpen] = useState(false);

  const togglePlaid = () => {
    setPlaidOpen((prev) => !prev);
  };

  const getBankName = () => {
    return `${
      paymentMethod.payment_method_nickname ||
      paymentMethod.institution_info?.institution_name ||
      paymentMethod.payment_method_data?.institution?.name ||
      "Unknown Institution"
    } ${capitalizeFirstLetter(
      paymentMethod.payment_method_nickname
        ? ""
        : paymentMethod.payment_method_data?.account?.subtype || ""
    )}`;
  };

  const canDisconnect = () => {
    const plaidConnectionsLeft = paymentMethodsQuery.data?.reduce(
      (acc, item) => {
        if (
          item.payment_method_type === "PLAID" &&
          item.payment_method_status === "active"
        ) {
          return acc + 1;
        }

        return acc;
      },
      0
    );

    if (plaidConnectionsLeft && plaidConnectionsLeft >= 2) return true;

    return false;
  };

  const handleMakePrimary = () => {
    updateDefaultPaymentMethod.mutate(
      {
        userId: paymentMethod.user_id,
        paymentMethodId: paymentMethod.payment_method_id,
      },
      {
        onSuccess: () => {
          toast.success("Your primary account has been updated");
        },
        onError: () => {
          toast.error(
            "There was an error setting default payment method. Please try again."
          );
        },
      }
    );
  };

  return (
    <div
      className={cn(
        "flex overflow-hidden bg-white shadow-sm rounded-xl w-full max-w-[370px] h-72 border-[3px] border-border",
        {
          "border-[#1DB45A]": paymentMethod?.payment_method_status === "active",
          "border-[#FFA500]": [
            "pending_manual_verification",
            "pending_automatic_verification",
            "pending",
            "database_insights_pending",
          ].includes(paymentMethod?.payment_method_status ?? ""),
        }
      )}
    >
      <div className={cn("flex flex-col justify-between p-6 md:p-8")}>
        <div>
          <div className="flex justify-between items-start">
            <div className={`max-w-10 h-auto`}>
              <img src={banks_logos["default"]} alt={`Bank Icon`} />
            </div>
            {paymentMethod.payment_method_default && (
              <div className="ml-2 inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                <svg
                  className="h-1.5 w-1.5 fill-green-500"
                  viewBox="0 0 6 6"
                  aria-hidden="true"
                >
                  <circle cx={3} cy={3} r={3} />
                </svg>
                Primary
                <HoverCard>
                  <HoverCardTrigger>
                    <Info className="w-4 h-4 text-gray-500 hover:text-gray-700" />
                  </HoverCardTrigger>
                  <HoverCardContent>
                    Your default account for payments and transfers.
                  </HoverCardContent>
                </HoverCard>
              </div>
            )}
          </div>
          <div className="flex items-center gap-x-3 mt-5 mb-2 w-full">
            <h3
              className="truncate text-xl font-medium text-gray-900 flex-1 min-w-0"
              style={{ maxWidth: "250px" }}
            >
              {getBankName()}
            </h3>
            {!isAdmin && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setAddNicknameModalOpen(true)}
              >
                <Edit className="h-4 w-4 " />
              </Button>
            )}
          </div>
          <p className="font-medium text-sm text-gray-700">{`****${paymentMethod.payment_method_data?.account?.mask}`}</p>
        </div>
        <div>
          <div className="flex gap-x-2 justify-between">
            {isAdmin ? (
              <div className="w-full rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-500">
                View only — contact the account owner to make changes.
              </div>
            ) : paymentMethod.payment_method_status === "pending_automatic_verification" ? (
              <div className="w-full rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                Your account is being verified automatically. This may take 1–2 business days.
              </div>
            ) : paymentMethod.payment_method_status === "pending_manual_verification" ? (
              <Button
                className="w-full bg-[#FFA500] hover:bg-[#e09400] text-white"
                onClick={togglePlaid}
              >
                Verify
              </Button>
            ) : paymentMethod.payment_method_status === "pending_disconnect" ? (
              <UpdatePaymentMethod
                paymentMethodId={paymentMethod.payment_method_id}
                name={paymentMethod.payment_method_data.name_on_account}
                paymentMethodDefault={paymentMethod.payment_method_default}
                className="w-full"
              />
            ) : (
              <>
                <PaymentMethodButtonStyled
                  //@ts-ignore
                  primary_color={platformQuery?.data?.primary_color || "#000"}
                  status={paymentMethod.payment_method_status}
                  handleDisconnect={() => {
                    if (!canDisconnect()) {
                      toast.error(
                        "Please link another account before disconnecting this account."
                      );
                      return;
                    }
                    setConfirmDisconnectOpen(true);
                  }}
                  handleConnect={togglePlaid}
                />
                {!paymentMethod.payment_method_default &&
                  paymentMethod.payment_method_status === "active" && (
                    <Button variant="outline" onClick={handleMakePrimary}>
                      Make primary
                    </Button>
                  )}
              </>
            )}
          </div>
          <AddNicknameModal
            open={addNicknameModalOpen}
            onOpenChange={setAddNicknameModalOpen}
            paymentMethodId={paymentMethod.payment_method_id}
            userId={paymentMethod.user_id}
          />
          <DisconnectAlertModal
            open={confirmDisconnectOpen}
            onOpenChange={setConfirmDisconnectOpen}
            paymentMethod={paymentMethod}
          />
          {plaidOpen && (
            //@ts-ignore
            <Plaid
              open={plaidOpen}
              onClose={togglePlaid}
              method_type={paymentMethod.payment_method_type}
              payment_method_status={paymentMethod.payment_method_status}
              access_token={paymentMethod.payment_method_data.access_token}
              name_on_account={
                paymentMethod.payment_method_data.name_on_account
              }
              linkData={{
                firstName: paymentMethod?.account_first_name,
                lastName: paymentMethod?.account_last_name,
                businessName: paymentMethod?.account_business_name,
                accountType: paymentMethod?.account_type,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

type DisconnectAlertModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentMethod: PaymentMethod;
};

function DisconnectAlertModal({
  open,
  onOpenChange,
  paymentMethod,
}: DisconnectAlertModalProps) {
  const disconnectMutation = useDisconnectPaymentMethod();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {/*@ts-ignore */}
      <AlertDialogContent>
        {/*@ts-ignore */}
        <AlertDialogHeader>
          {/*@ts-ignore */}
          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
          {/*@ts-ignore */}
          <AlertDialogDescription>
            {`Are you sure you'd like to disconnect ${
              paymentMethod?.payment_method_nickname ||
              paymentMethod.institution_info?.institution_name ||
              paymentMethod.payment_method_data?.institution?.name ||
              "Unknown Institution"
            } ending in ${paymentMethod.payment_method_data?.account?.mask}?`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {/*@ts-ignore */}
        <AlertDialogFooter>
          {/*@ts-ignore */}
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {/*@ts-ignore */}
          <Button
            onClick={() => {
              disconnectMutation.mutate(
                {
                  userId: paymentMethod.user_id,
                  type: paymentMethod.payment_method_type,
                  paymentMethodId: paymentMethod.payment_method_id,
                },
                {
                  onSettled: () => onOpenChange(false),
                  onSuccess: () =>
                    toast.success(
                      `Your connected bank account has been removed successfully.`
                    ),
                }
              );
            }}
          >
            {disconnectMutation.isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Continue"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const NicknameFormSchema = z.object({
  nickname: z.string().min(1, "Nickname is required"),
});

function AddNicknameModal({ open, onOpenChange, userId, paymentMethodId }) {
  const {
    handleSubmit,
    register,
    formState: { isValid },
    reset,
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(NicknameFormSchema),
  });
  const updatePaymentMethod = useUpdatePaymentMethod();
  const saveDisabled = !isValid || updatePaymentMethod.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*@ts-ignore */}
      <DialogContent className="sm:max-w-[425px]">
        <form
          onSubmit={handleSubmit(async (values) => {
            const payload = {
              payment_method_id: paymentMethodId,
              user_id: userId,
              payment_method_nickname: values.nickname,
            };

            updatePaymentMethod.mutate(payload, {
              onSuccess: () => {
                reset();
                onOpenChange(false);
              },
            });
          })}
        >
          {/*@ts-ignore */}
          <DialogHeader>
            {/*@ts-ignore */}
            <DialogTitle className="text-xl">
              <div>Add Account Nickname</div>
            </DialogTitle>
            {/*@ts-ignore */}
            <DialogDescription>
              Depending on the method used to link your account the institution
              information might be missing. Add a nickname so you can better
              identify the linked account.
            </DialogDescription>
          </DialogHeader>

          <div className="pt-4">
            {/* @ts-ignore */}
            <Label htmlFor="nickname">Account Nickname</Label>
            <Input {...register("nickname")} />
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              className="w-1/2"
              type="button"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button className="w-1/2" type="submit" disabled={saveDisabled}>
              {updatePaymentMethod.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
